import * as React from 'react';

import { Tab } from '@headlessui/react';

import { clsx } from '@freelancelabs/utils';

export type TabsVariant = 'default' | 'segmented' | 'fiche';

export interface TabsProps {
    tabs: { label: string; content: React.ReactElement; count?: number }[];
    selectedIndex?: number;
    onChange?: (arg: number) => void;
    /**
     * Controls how the tab list looks. 'segmented' renders pill shaped tabs,
     * sized to sit next to filters inside a toolbar: it carries the cream filter
     * bar of the list pages. 'fiche' renders the tab bar of a detail sheet: pills
     * that scroll horizontally, since a sheet can have up to seven of them.
     */
    variant?: TabsVariant;
    /**
     * Filters or actions rendered on the same row as the tab list.
     */
    toolbar?: React.ReactNode;
    /**
     * Wraps the tab BAR, so the caller can put it where it belongs while the panels
     * stay below.
     *
     * **A detail sheet needs it and nothing else does.** The V3 mockup puts its tab bar
     * INSIDE the header card, under the title and the status, with the panels below the
     * card. The bar and the panels must therefore sit in the same `Tab.Group` while
     * living in two different places in the tree, which no prop of the shape
     * « render the bar here » can express: the wrapper is the caller's, the group is
     * ours.
     *
     * Left out, nothing changes for the callers that had none.
     */
    wrapList?: (list: React.ReactNode) => React.ReactNode;

    /** Rend le `toolbar` AVANT la liste d'onglets : les filtres d'abord, la bascule apres. */
    toolbarLeading?: boolean;
}

const Tabs = ({ tabs, selectedIndex, onChange, variant = 'default', toolbar, wrapList, toolbarLeading }: TabsProps) => {
    const segmented = variant === 'segmented';
    const fiche = variant === 'fiche';
    const toolbarNode = toolbar && <div className="fl-tabs__toolbar">{toolbar}</div>;
    const list = (
        <div
            className={clsx(
                'fl-tabs',
                segmented && 'fl-tabs--segmented',
                fiche && 'fl-tabs--fiche',
                toolbarLeading && 'fl-tabs--toolbar-leading'
            )}
        >
            {toolbarLeading && toolbarNode}
            <Tab.List
                className={clsx(
                    'fl-tabs__list',
                    segmented && 'fl-tabs__list--segmented',
                    fiche && 'fl-tabs__list--fiche'
                )}
            >
                {tabs.map((tab) => (
                    <Tab key={tab.label}>
                        {tab.label}
                        {tab.count !== undefined && <span className="fl-tabs__count">{tab.count}</span>}
                    </Tab>
                ))}
            </Tab.List>
            {!toolbarLeading && toolbarNode}
        </div>
    );

    return (
        <Tab.Group selectedIndex={selectedIndex} onChange={onChange}>
            {wrapList ? wrapList(list) : list}
            <Tab.Panels as={React.Fragment}>
                {tabs.map((tab, index) => (
                    <Tab.Panel key={index}>{tab.content}</Tab.Panel>
                ))}
            </Tab.Panels>
        </Tab.Group>
    );
};

export default Tabs;
